import { applyWaveYjsRuntimeFlag, normalizeRealtimeWsUrl } from "@vida/wave-core";

export const RUNTIME_CONFIG_PATH = "/vida-cloud-wave-config.json";

export type RuntimeConfig = {
  appsyncEndpoint: string;
  appsyncRealtimeEndpoint?: string;
  /** GET /client-config on API Gateway (not AppSync). */
  clientConfigUrl?: string;
  cognitoDomain: string;
  cognitoClientId: string;
  oauthRedirectUri: string;
  oauthPostLogoutRedirectUri?: string;
  sessionBrokerUrl?: string;
  loginUrl?: string;
  /** Canonical SPA origin for share links and push deep links (e.g. https://wave.oceany.ai). */
  publicBaseUrl?: string;
  /** When true, enables Yjs char-level editing (same as cloud `wave_yjs_enabled`). */
  waveYjsEnabled?: boolean;
  /** Whale / Oceany stream (prod bootstrap, allineato a client-config). */
  oceanAgentStreamUrl?: string;
  waveWhaleAgentId?: string;
  cognitoIdentityPoolId?: string;
  cognitoUserPoolId?: string;
  awsRegion?: string;
};

export type RuntimeOceanBootstrap = {
  streamUrl: string;
  whaleAgentId: string;
  identityPoolId: string;
  userPoolId: string;
  awsRegion: string;
};

let realtimeEndpointOverride: string | null = null;

let runtime: RuntimeConfig | null = null;
let endpointReadyResolve: (() => void) | null = null;
const endpointReadyPromise: Promise<void> = new Promise((resolve) => {
  endpointReadyResolve = resolve;
});

function notifyEndpointReady(): void {
  endpointReadyResolve?.();
  endpointReadyResolve = null;
}

export function waitForEndpointReady(): Promise<void> {
  if (getAppsyncEndpoint()) {
    notifyEndpointReady();
    return Promise.resolve();
  }
  if (import.meta.env.DEV) {
    notifyEndpointReady();
    return Promise.resolve();
  }
  return endpointReadyPromise;
}

function fromEnv(key: string): string {
  const raw = import.meta.env as Record<string, string | undefined>;
  return typeof raw[key] === "string" ? raw[key]!.trim() : "";
}

function applyRuntimeWaveFlags(cfg: RuntimeConfig | null): void {
  if (cfg?.waveYjsEnabled === true) {
    applyWaveYjsRuntimeFlag(true);
  }
}

export async function ensureConfig(): Promise<void> {
  if (import.meta.env.DEV) {
    notifyEndpointReady();
    return;
  }
  if (runtime) {
    notifyEndpointReady();
    return;
  }
  try {
    const res = await fetch(RUNTIME_CONFIG_PATH, { cache: "no-store" });
    if (res.ok) {
      runtime = (await res.json()) as RuntimeConfig;
      applyRuntimeWaveFlags(runtime);
      notifyEndpointReady();
      return;
    }
  } catch {
    /* fallback */
  }
  if (
    fromEnv("VITE_APPSYNC_ENDPOINT") &&
    fromEnv("VITE_COGNITO_DOMAIN") &&
    fromEnv("VITE_COGNITO_CLIENT_ID") &&
    fromEnv("VITE_OAUTH_REDIRECT_URI")
  ) {
    notifyEndpointReady();
    return;
  }
  throw new Error(
    `Config mancante: serve ${RUNTIME_CONFIG_PATH} o variabili VITE_* in build.`,
  );
}

export function getAppsyncEndpoint(): string {
  return (
    runtime?.appsyncEndpoint?.trim() || fromEnv("VITE_APPSYNC_ENDPOINT")
  ).replace(/\/$/, "");
}

export function setAppsyncRealtimeEndpoint(url: string): void {
  realtimeEndpointOverride = normalizeRealtimeWsUrl(url);
}

export function getAppsyncRealtimeEndpoint(): string {
  if (realtimeEndpointOverride) return realtimeEndpointOverride;
  const fromRuntime = runtime?.appsyncRealtimeEndpoint?.trim();
  if (fromRuntime) return normalizeRealtimeWsUrl(fromRuntime);
  const env = fromEnv("VITE_APPSYNC_REALTIME_ENDPOINT");
  if (env) return normalizeRealtimeWsUrl(env);
  const gql = getAppsyncEndpoint();
  return normalizeRealtimeWsUrl(gql.replace("appsync-api", "appsync-realtime-api"));
}

export function getClientConfigUrl(): string {
  const fromRuntime = runtime?.clientConfigUrl?.trim();
  if (fromRuntime) return fromRuntime.replace(/\/$/, "");
  return fromEnv("VITE_CLIENT_CONFIG_URL").replace(/\/$/, "");
}

export function getCognitoDomain(): string {
  return (
    runtime?.cognitoDomain?.trim() || fromEnv("VITE_COGNITO_DOMAIN")
  ).replace(/\/$/, "");
}

export function getClientId(): string {
  return runtime?.cognitoClientId?.trim() || fromEnv("VITE_COGNITO_CLIENT_ID");
}

export function getRedirectUri(): string {
  return (
    runtime?.oauthRedirectUri?.trim() || fromEnv("VITE_OAUTH_REDIRECT_URI")
  );
}

export function getPublicBaseUrl(): string {
  const fromRuntime = runtime?.publicBaseUrl?.trim();
  if (fromRuntime) return fromRuntime.replace(/\/$/, "");
  const env = fromEnv("VITE_PUBLIC_BASE_URL");
  if (env) return env.replace(/\/$/, "");
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

export function getSessionBrokerUrl(): string {
  return runtime?.sessionBrokerUrl?.trim() || fromEnv("VITE_SESSION_BROKER_URL");
}

export function getLoginUrl(): string {
  return runtime?.loginUrl?.trim() || fromEnv("VITE_LOGIN_URL");
}

export function getPostLogoutRedirect(): string {
  const login = getLoginUrl();
  if (login) return `${login.replace(/\/$/, "")}/login`;
  const fromRuntime = runtime?.oauthPostLogoutRedirectUri?.trim();
  if (fromRuntime) return fromRuntime;
  const envLogout = fromEnv("VITE_OAUTH_POST_LOGOUT_REDIRECT");
  if (envLogout) return envLogout;
  return "https://login.oceany.ai/login";
}

export function isConfigured(): boolean {
  return Boolean(
    getAppsyncEndpoint() &&
      getCognitoDomain() &&
      getClientId(),
  );
}

/** Campi Whale da `vida-cloud-wave-config.json` (prod, senza attendere client-config). */
export function getRuntimeOceanBootstrap(): RuntimeOceanBootstrap {
  return {
    streamUrl: runtime?.oceanAgentStreamUrl?.trim() || "",
    whaleAgentId: runtime?.waveWhaleAgentId?.trim() || "",
    identityPoolId: runtime?.cognitoIdentityPoolId?.trim() || "",
    userPoolId: runtime?.cognitoUserPoolId?.trim() || "",
    awsRegion: runtime?.awsRegion?.trim() || "",
  };
}
